import {
  Shield, Car, Home, FileText, CreditCard, Bell,
  CheckCircle2, Clock, Download, Star, Calendar, Phone,
  Mail, Settings, LogOut, User, Menu, X, Lock, Coins,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SidebarLogo, MobileHeaderLogo, MobileMenuLogo } from "../components/layout/LogoVariants";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import BannerCarousel from "../components/client/BannerCarousel";
import ReferralForm from "../components/client/ReferralForm";
import ConsortiumDashboard from "../components/client/ConsortiumDashboard";
import ChangePasswordModal from "../components/client/ChangePasswordModal";
import ClientLoyaltyCard from "../components/client/ClientLoyaltyCard";
import ClientDoletasCard from "../components/client/ClientDoletasCard";
import ClientDocuments from "../components/client/ClientDocuments";
import LgpdConsentModal from "../components/client/LgpdConsentModal";
import InstallPWAPrompt from "../components/client/InstallPWAPrompt";
import CompleteProfileModal from "../components/client/CompleteProfileModal";

function getInitials(name) {
  if (!name) return "C";
  var parts = name.split(" ");
  var a = parts[0] ? parts[0][0] : "";
  var b = parts[1] ? parts[1][0] : "";
  return (a + b).toUpperCase();
}

var PRODUCT_ICONS = { consorcio: Car, seguro: Shield, carta_contemplada: Star };
var PRODUCT_ICON_COLORS = { consorcio: "text-[#F26A4B]", seguro: "text-[#3B82F6]", carta_contemplada: "text-emerald-500" };
var PRODUCT_BG_COLORS = { consorcio: "bg-[#F26A4B]/10", seguro: "bg-[#3B82F6]/10", carta_contemplada: "bg-emerald-500/10" };
var NOTIF_ICONS = { boleto: CreditCard, renovacao: Calendar, documento: CheckCircle2, campanha: Star, geral: Bell };
var NOTIF_COLORS = { boleto: "bg-amber-50 border-amber-100", renovacao: "bg-blue-50 border-blue-100", documento: "bg-emerald-50 border-emerald-100", campanha: "bg-purple-50 border-purple-100", geral: "bg-gray-50 border-gray-100" };
var NOTIF_ICON_WRAP = { boleto: "bg-amber-100", renovacao: "bg-blue-100", documento: "bg-emerald-100", campanha: "bg-purple-100", geral: "bg-gray-100" };
var NOTIF_ICON_COLOR = { boleto: "text-amber-600", renovacao: "text-[#3B82F6]", documento: "text-emerald-600", campanha: "text-purple-600", geral: "text-gray-600" };

function formatCurrency(v) {
  if (v == null || v === 0) return "R$ 0";
  return "R$ " + v.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function ProductCard({ product }) {
  var Icon = PRODUCT_ICONS[product.product_type] || FileText;
  var iconColor = PRODUCT_ICON_COLORS[product.product_type] || "text-gray-500";
  var bgColor = PRODUCT_BG_COLORS[product.product_type] || "bg-gray-100";
  var isConsorcio = product.product_type === "consorcio";

  var STATUS_MAP = {
    ativo: { cls: "bg-emerald-100 text-emerald-700", label: "Ativo" },
    pendente: { cls: "bg-amber-100 text-amber-700", label: "Pendente" },
    contemplado: { cls: "bg-blue-100 text-blue-700", label: "Contemplado" },
    em_atraso: { cls: "bg-red-100 text-red-700", label: "Em Atraso" },
    cancelado: { cls: "bg-gray-200 text-gray-600", label: "Cancelado" },
    encerrado: { cls: "bg-gray-200 text-gray-600", label: "Encerrado" },
  };
  var st = STATUS_MAP[product.status] || { cls: "bg-gray-100 text-gray-600", label: product.status };

  return (
    <div className="flex flex-col p-3 lg:p-4 bg-gray-50 rounded-xl border border-gray-100 gap-3" data-testid={"product-card-" + product.id}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 lg:gap-4">
          <div className={"w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center flex-shrink-0 " + bgColor}>
            <Icon className={"w-5 h-5 lg:w-6 lg:h-6 " + iconColor} />
          </div>
          <div>
            <p className="font-semibold text-[#0A2F3D] text-sm lg:text-base">{product.description}</p>
            {isConsorcio ? (
              <p className="text-xs lg:text-sm text-gray-500">
                Carta: {formatCurrency(product.value)} - {formatCurrency(product.installment_value)}/mes
              </p>
            ) : (
              <p className="text-xs lg:text-sm text-gray-500">
                {product.details}{product.expiry_date ? " - Ate " + product.expiry_date : ""}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
          {isConsorcio ? (
            <div className="text-left sm:text-right">
              <p className="text-xs text-gray-400">Parcelas</p>
              <p className="font-semibold text-[#0A2F3D] text-sm">{product.installments_paid}/{product.installments_total}</p>
            </div>
          ) : (
            <div className="text-left sm:text-right">
              <p className="text-xs text-gray-400">Valor</p>
              <p className="font-semibold text-[#0A2F3D] text-sm">{formatCurrency(product.value)}</p>
            </div>
          )}
          <span className={"px-2 lg:px-3 py-1 text-xs font-medium rounded-full flex items-center gap-1 " + st.cls}>
            <CheckCircle2 className="w-3 h-3" />{st.label}
          </span>
        </div>
      </div>
      {isConsorcio && (product.grupo || product.cota) && (
        <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
          {product.grupo && (
            <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-[#F26A4B]/10 text-[#F26A4B]">Grupo: {product.grupo}</span>
          )}
          {product.cota && (
            <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-[#3B82F6]/10 text-[#3B82F6]">Cota: {product.cota}</span>
          )}
          {product.administrator && (
            <span className="text-xs text-gray-500">{product.administrator}</span>
          )}
          {product.start_date && (
            <span className="text-xs text-gray-400">Inicio: {product.start_date}</span>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationCard({ notif }) {
  var Icon = NOTIF_ICONS[notif.notification_type] || Bell;
  var wrapCls = NOTIF_COLORS[notif.notification_type] || "bg-gray-50 border-gray-100";
  var iconWrap = NOTIF_ICON_WRAP[notif.notification_type] || "bg-gray-100";
  var iconColor = NOTIF_ICON_COLOR[notif.notification_type] || "text-gray-600";

  return (
    <div className={"flex items-start gap-3 p-3 rounded-xl border " + wrapCls} data-testid={"notif-card-" + notif.id}>
      <div className={"p-2 rounded-lg flex-shrink-0 " + iconWrap}>
        <Icon className={"w-4 h-4 " + iconColor} />
      </div>
      <div className="min-w-0">
        <p className="text-xs lg:text-sm font-medium text-gray-800">{notif.title}</p>
        <p className="text-xs text-gray-500 truncate">{notif.message}</p>
      </div>
    </div>
  );
}

function ClientSidebar({ user, broker, activePage, productsCount, notifsCount, onChangePassword, loyaltyEnabled }) {
  var initials = getInitials(user ? user.name : null);
  var auth = useAuth();

  function scrollTo(id) {
    var el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <aside className="hidden lg:flex w-64 xl:w-72 bg-[#0A2F3D] flex-col fixed h-full z-20">
      <div className="p-5 xl:p-6 border-b border-white/10">
        <SidebarLogo />
      </div>
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#3B82F6] flex items-center justify-center ring-2 ring-[#F26A4B]/30">
            <span className="text-white font-semibold">{initials}</span>
          </div>
          <div>
            <p className="text-white font-medium">{user ? user.name : ""}</p>
            <p className="text-white/50 text-xs">{user ? user.email : ""}</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#3B82F6] text-white cursor-pointer shadow-lg shadow-blue-500/20" data-testid="client-nav-inicio" onClick={function() { window.scrollTo({ top: 0, behavior: "smooth" }); }}>
          <Home className="w-5 h-5" /><span className="font-medium">Inicio</span>
        </div>
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 cursor-pointer transition-colors" data-testid="client-nav-products" onClick={function() { scrollTo("section-products"); }}>
          <FileText className="w-5 h-5" /><span className="font-medium">Meus Produtos</span>
          {productsCount > 0 && <span className="ml-auto bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">{productsCount}</span>}
        </div>
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 cursor-pointer transition-colors" data-testid="client-nav-consortiums" onClick={function() { scrollTo("section-consortiums"); }}>
          <Car className="w-5 h-5" /><span className="font-medium">Consorcios</span>
        </div>
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 cursor-pointer transition-colors" data-testid="client-nav-documents" onClick={function() { scrollTo("section-documents"); }}>
          <FileText className="w-5 h-5" /><span className="font-medium">Documentos</span>
        </div>
        {loyaltyEnabled && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 cursor-pointer transition-colors" data-testid="client-nav-loyalty" onClick={function() { scrollTo("section-loyalty"); }}>
            <Coins className="w-5 h-5" /><span className="font-medium">DOLETAS</span>
          </div>
        )}
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 cursor-pointer transition-colors" data-testid="client-nav-notifications" onClick={function() { scrollTo("section-notifications"); }}>
          <Bell className="w-5 h-5" /><span className="font-medium">Notificacoes</span>
          {notifsCount > 0 && <span className="ml-auto bg-[#F26A4B] text-white text-xs px-2 py-0.5 rounded-full font-medium">{notifsCount}</span>}
        </div>
      </nav>
      {broker && (
        <div className="p-4 border-t border-white/10">
          <div className="bg-white/5 rounded-xl p-4 mb-4">
            <p className="text-white/80 text-sm font-medium mb-2">Seu Consultor</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#3B82F6]/20 flex items-center justify-center ring-2 ring-[#F26A4B]/20">
                <User className="w-5 h-5 text-[#3B82F6]" />
              </div>
              <div>
                <p className="text-white text-sm font-medium" data-testid="broker-name">{broker.name}</p>
                <p className="text-[#F26A4B] text-xs font-medium">{broker.specialty || "Geral"}</p>
              </div>
            </div>
          </div>
          <button onClick={function() { onChangePassword(); }} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-white/60 hover:bg-white/5 transition-colors" data-testid="client-change-password">
            <Lock className="w-5 h-5" /><span className="font-medium">Alterar Senha</span>
          </button>
          <button onClick={function() { auth.logout(); }} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-white/60 hover:bg-red-500/10 hover:text-red-400 transition-colors" data-testid="client-logout">
            <LogOut className="w-5 h-5" /><span className="font-medium">Sair</span>
          </button>
        </div>
      )}
    </aside>
  );
}

function ClientMobileMenu({ isOpen, onClose, user, broker, productsCount, notifsCount, onChangePassword, loyaltyEnabled }) {
  var initials = getInitials(user ? user.name : null);
  var auth = useAuth();
  if (!isOpen) return null;

  function scrollTo(id) {
    var el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="fixed left-0 top-0 bottom-0 w-72 bg-[#0A2F3D] p-4 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <MobileMenuLogo />
          <button onClick={onClose} className="p-2 text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-3 mb-4 bg-white/5 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#3B82F6] flex items-center justify-center">
              <span className="text-white font-semibold text-sm">{initials}</span>
            </div>
            <div>
              <p className="text-white font-medium text-sm">{user ? user.name : ""}</p>
              <p className="text-white/50 text-xs">{user ? user.email : ""}</p>
            </div>
          </div>
        </div>
        <nav className="space-y-1">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#3B82F6] text-white cursor-pointer" onClick={function() { window.scrollTo({ top: 0, behavior: "smooth" }); onClose(); }}>
            <Home className="w-5 h-5" /><span className="font-medium">Inicio</span>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 cursor-pointer" onClick={function() { scrollTo("section-products"); }}>
            <FileText className="w-5 h-5" /><span className="font-medium">Meus Produtos</span>
            {productsCount > 0 && <span className="ml-auto bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">{productsCount}</span>}
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 cursor-pointer" onClick={function() { scrollTo("section-consortiums"); }}>
            <Car className="w-5 h-5" /><span className="font-medium">Consorcios</span>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 cursor-pointer" onClick={function() { scrollTo("section-documents"); }}>
            <FileText className="w-5 h-5" /><span className="font-medium">Documentos</span>
          </div>
          {loyaltyEnabled && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 cursor-pointer" onClick={function() { scrollTo("section-loyalty"); }}>
              <Coins className="w-5 h-5" /><span className="font-medium">DOLETAS</span>
            </div>
          )}
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 cursor-pointer" onClick={function() { scrollTo("section-notifications"); }}>
            <Bell className="w-5 h-5" /><span className="font-medium">Notificacoes</span>
            {notifsCount > 0 && <span className="ml-auto bg-[#F26A4B] text-white text-xs px-2 py-0.5 rounded-full">{notifsCount}</span>}
          </div>
        </nav>
        {broker && (
          <div className="mt-6 p-4 bg-white/5 rounded-xl">
            <p className="text-white/80 text-sm font-medium mb-2">Seu Consultor</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#3B82F6]/20 flex items-center justify-center">
                <User className="w-5 h-5 text-[#3B82F6]" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">{broker.name}</p>
                <p className="text-white/50 text-xs">{broker.specialty || "Geral"}</p>
              </div>
            </div>
          </div>
        )}
        <button onClick={function() { onChangePassword(); onClose(); }} className="w-full flex items-center gap-3 px-4 py-3 mt-4 rounded-xl text-white/60 hover:bg-white/5">
          <Lock className="w-5 h-5" /><span className="font-medium">Alterar Senha</span>
        </button>
        <button onClick={function() { auth.logout(); }} className="w-full flex items-center gap-3 px-4 py-3 mt-1 rounded-xl text-white/60 hover:bg-red-500/10 hover:text-red-400">
          <LogOut className="w-5 h-5" /><span className="font-medium">Sair</span>
        </button>
      </div>
    </div>
  );
}

export default function ClientAreaPage() {
  var auth = useAuth();
  var user = auth.user;
  var mobileMenuState = useState(false);
  var mobileMenuOpen = mobileMenuState[0];
  var setMobileMenuOpen = mobileMenuState[1];

  var summaryState = useState(null);
  var summary = summaryState[0];
  var setSummary = summaryState[1];
  var productsState = useState([]);
  var products = productsState[0];
  var setProducts = productsState[1];
  var notifsState = useState([]);
  var notifs = notifsState[0];
  var setNotifs = notifsState[1];
  var brokerState = useState(null);
  var broker = brokerState[0];
  var setBroker = brokerState[1];
  var loyaltyState = useState(null);
  var loyalty = loyaltyState[0];
  var setLoyalty = loyaltyState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var fetchData = useCallback(async function() {
    setLoading(true);
    try {
      var sRes = await api.get("/client/summary");
      setSummary(sRes.data);
      var pRes = await api.get("/client/products");
      setProducts(pRes.data.products || []);
      var nRes = await api.get("/client/notifications");
      setNotifs(nRes.data.notifications || []);
      var bRes = await api.get("/client/broker");
      setBroker(bRes.data.broker || null);
      var lRes = await api.get("/client/loyalty");
      setLoyalty(lRes.data);
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function() { fetchData(); }, [fetchData]);

  var userName = user ? user.name : "Cliente";
  var firstName = userName ? userName.split(" ")[0] : "Cliente";
  var productsCount = summary ? summary.products_active : 0;
  var notifsCount = summary ? summary.notifications_unread : 0;

  var productCards = [];
  for (var i = 0; i < products.length; i++) {
    productCards.push(<ProductCard key={products[i].id} product={products[i]} />);
  }

  var notifCards = [];
  for (var j = 0; j < notifs.length; j++) {
    notifCards.push(<NotificationCard key={notifs[j].id} notif={notifs[j]} />);
  }

  var showPwdState = useState(false);
  function openPwd() { showPwdState[1](true); }

  var showLgpd = user && user.role === "client" && !user.lgpd_accepted;
  var showOnboarding = user && user.role === "client" && user.lgpd_accepted && !user.onboarding_completed;

  function handleLgpdAccepted() {
    auth.checkAuth();
  }

  function handleProfileCompleted() {
    auth.checkAuth();
  }

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="client-area-page">
      {showLgpd && <LgpdConsentModal onAccepted={handleLgpdAccepted} />}
      {showOnboarding && <CompleteProfileModal onCompleted={handleProfileCompleted} />}
      {showPwdState[0] && <ChangePasswordModal onClose={function() { showPwdState[1](false); }} />}
      <ClientSidebar user={user} broker={broker} activePage="inicio" productsCount={productsCount} notifsCount={notifsCount} onChangePassword={openPwd} loyaltyEnabled={loyalty && loyalty.enabled} />
      <ClientMobileMenu isOpen={mobileMenuOpen} onClose={function() { setMobileMenuOpen(false); }} user={user} broker={broker} productsCount={productsCount} notifsCount={notifsCount} onChangePassword={openPwd} loyaltyEnabled={loyalty && loyalty.enabled} />

      <main className="flex-1 lg:ml-64 xl:ml-72">
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <button className="lg:hidden p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg" onClick={function() { setMobileMenuOpen(true); }} data-testid="client-mobile-menu">
              <Menu className="w-6 h-6" />
            </button>
            <div className="hidden sm:block">
              <h1 className="text-xl lg:text-2xl font-bold text-[#0A2F3D]" data-testid="client-greeting">Olá, {firstName}!</h1>
              <p className="text-sm text-gray-500">Bem-vindo(a) à sua área exclusiva</p>
            </div>
            <div className="lg:hidden"><MobileHeaderLogo /></div>
            <div className="flex items-center gap-2 sm:gap-4">
              <button className="relative p-2 sm:p-2.5 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-testid="client-notifications-bell">
                <Bell className="w-5 h-5 text-gray-600" />
                {notifsCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F26A4B] text-white text-xs rounded-full flex items-center justify-center font-medium">{notifsCount}</span>}
              </button>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-8">
          <div className="sm:hidden mb-4">
            <h1 className="text-xl font-bold text-[#0A2F3D]">Olá, {firstName}!</h1>
            <p className="text-sm text-gray-500">Bem-vindo(a) à sua área</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <BannerCarousel />

              {/* KPIs */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-6 lg:mb-8">
                <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-3">
                    <div className="p-2 lg:p-2.5 bg-[#3B82F6]/10 rounded-lg lg:rounded-xl"><FileText className="w-4 h-4 lg:w-5 lg:h-5 text-[#3B82F6]" /></div>
                    <span className="text-xs lg:text-sm text-gray-500">Produtos Ativos</span>
                  </div>
                  <p className="text-2xl lg:text-3xl font-bold text-[#0A2F3D]" data-testid="kpi-products">{productsCount}</p>
                </div>
                <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-3">
                    <div className="p-2 lg:p-2.5 bg-[#F26A4B]/10 rounded-lg lg:rounded-xl"><Bell className="w-4 h-4 lg:w-5 lg:h-5 text-[#F26A4B]" /></div>
                    <span className="text-xs lg:text-sm text-gray-500">Notificações</span>
                  </div>
                  <p className="text-2xl lg:text-3xl font-bold text-[#0A2F3D]" data-testid="kpi-notifications">{notifsCount}</p>
                </div>
                <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm border border-gray-100 col-span-2 lg:col-span-1">
                  <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-3">
                    <div className="p-2 lg:p-2.5 bg-emerald-500/10 rounded-lg lg:rounded-xl"><Calendar className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-500" /></div>
                    <span className="text-xs lg:text-sm text-gray-500">Agendamentos</span>
                  </div>
                  <p className="text-2xl lg:text-3xl font-bold text-[#0A2F3D]" data-testid="kpi-schedules">{summary ? summary.upcoming_schedules : 0}</p>
                </div>
              </div>

              <ConsortiumDashboard products={products} />

              <div id="section-documents" className="scroll-mt-24">
                <ClientDocuments />
              </div>

              <div id="section-loyalty" className="scroll-mt-24">
                <ClientDoletasCard />
              </div>

              {/* Products + Notifications */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6 lg:mb-8">
                <div id="section-products" className="lg:col-span-2 bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm border border-gray-100 scroll-mt-24">
                  <h3 className="text-base lg:text-lg font-bold text-[#0A2F3D] mb-4 lg:mb-6">Meus Produtos</h3>
                  {products.length === 0 ? (
                    <p className="text-sm text-gray-400 py-8 text-center">Nenhum produto ativo</p>
                  ) : (
                    <div className="space-y-3 lg:space-y-4" data-testid="products-list">{productCards}</div>
                  )}
                </div>
                <div id="section-notifications" className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm border border-gray-100 scroll-mt-24">
                  <h3 className="text-base lg:text-lg font-bold text-[#0A2F3D] mb-4 lg:mb-6">Avisos</h3>
                  {notifs.length === 0 ? (
                    <p className="text-sm text-gray-400 py-8 text-center">Nenhuma notificação</p>
                  ) : (
                    <div className="space-y-3 lg:space-y-4" data-testid="notifications-list">{notifCards}</div>
                  )}
                </div>
              </div>

              {/* Broker Card */}
              {broker && (
                <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm border border-gray-100" data-testid="broker-card">
                  <h3 className="text-base lg:text-lg font-bold text-[#0A2F3D] mb-4">Seu Consultor</h3>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-[#3B82F6]/10 flex items-center justify-center ring-2 ring-[#F26A4B]/20">
                      <User className="w-7 h-7 text-[#3B82F6]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0A2F3D] text-base" data-testid="broker-card-name">{broker.name}</p>
                      <p className="text-sm text-gray-500">{broker.specialty || "Geral"}</p>
                      {broker.email && <p className="text-xs text-gray-400 flex items-center gap-1 mt-1"><Mail className="w-3 h-3" />{broker.email}</p>}
                    </div>
                  </div>
                </div>
              )}

              <ReferralForm />
            </>
          )}
        </div>
      </main>
      <InstallPWAPrompt />
    </div>
  );
}
